import React, { useState } from "react";
import { Button, Card, Col, Row, Form, InputGroup, Tab } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AuthService } from "../../../../authentication/service";
import { ErrorToast, SuccessToast } from "../../../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const schema = yup.object().shape({
    oldPassword: yup.string().notRequired(),
    newPassword: yup.string().notRequired(),
    confirmPassword: yup.string()
        .oneOf([yup.ref('newPassword'), undefined], 'Passwords must match')
        .notRequired()
});

interface ChangePasswordFormState {
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
}

const ChangePassword: React.FC = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false
    });
    const togglePassword = (field: keyof typeof showPassword) =>
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
    const { control, handleSubmit, formState: { errors } } = useForm<ChangePasswordFormState>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            oldPassword: "",
            newPassword: "",
            confirmPassword: ""
        }
    });

    const onSubmit = async (data: ChangePasswordFormState) => {
        try {
            await AuthService.changePassword({
                oldPassword: data.oldPassword,
                newPassword: data.newPassword
            });
            SuccessToast(t('user.passwordChanged'));
            navigate('/profile-overview');
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Tab.Pane eventKey="ChangePassword" className="fade" id="user-set-password" role="tabpanel"
                aria-labelledby="user-set-password-tab">
                <Row>
                    <Col sm={12}>
                        <Card>
                            <Card.Header>
                                <h5>{t('user.changePassword')}</h5>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item pt-0 px-0">
                                            <Row className="mb-0">
                                                <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                                                    {t('user.currentPassword')} <span className="text-danger">*</span>
                                                </label>
                                                <Col md={8} sm={12}>
                                                    <Controller
                                                        name="oldPassword"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <InputGroup hasValidation>
                                                                <Form.Control
                                                                    type={showPassword.oldPassword ? "text" : "password"}
                                                                    isInvalid={!!errors.oldPassword}
                                                                    placeholder={t('user.enterCurrentPassword')}
                                                                    {...field}
                                                                />
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    type="button"
                                                                    tabIndex={-1}
                                                                    onClick={() => togglePassword('oldPassword')}
                                                                >
                                                                    <i className={`ti ${showPassword.oldPassword ? "ti-eye-off" : "ti-eye"}`}></i>
                                                                </Button>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.oldPassword?.message}
                                                                </Form.Control.Feedback>
                                                            </InputGroup>
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </li>
                                        <li className="list-group-item px-0">
                                            <Row className="mb-0">
                                                <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                                                    {t('user.newPassword')} <span className="text-danger">*</span>
                                                </label>
                                                <Col md={8} sm={12}>
                                                    <Controller
                                                        name="newPassword"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <InputGroup hasValidation>
                                                                <Form.Control
                                                                    type={showPassword.newPassword ? "text" : "password"}
                                                                    isInvalid={!!errors.newPassword}
                                                                    placeholder={t('user.enterNewPassword')}
                                                                    {...field}
                                                                />
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    type="button"
                                                                    tabIndex={-1}
                                                                    onClick={() => togglePassword('newPassword')}
                                                                >
                                                                    <i className={`ti ${showPassword.newPassword ? "ti-eye-off" : "ti-eye"}`}></i>
                                                                </Button>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.newPassword?.message}
                                                                </Form.Control.Feedback>
                                                            </InputGroup>
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </li>
                                        <li className="list-group-item pb-0 px-0">
                                            <Row className="mb-0">
                                                <label className="col-form-label col-md-4 col-sm-12 text-md-end">
                                                    {t('user.confirmPassword')} <span className="text-danger">*</span>
                                                </label>
                                                <Col md={8} sm={12}>
                                                    <Controller
                                                        name="confirmPassword"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <InputGroup hasValidation>
                                                                <Form.Control
                                                                    type={showPassword.confirmPassword ? "text" : "password"}
                                                                    isInvalid={!!errors.confirmPassword}
                                                                    placeholder={t('user.confirmPasswordPlaceholder')}
                                                                    {...field}
                                                                />
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    type="button"
                                                                    tabIndex={-1}
                                                                    onClick={() => togglePassword('confirmPassword')}
                                                                >
                                                                    <i className={`ti ${showPassword.confirmPassword ? "ti-eye-off" : "ti-eye"}`}></i>
                                                                </Button>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.confirmPassword?.message}
                                                                </Form.Control.Feedback>
                                                            </InputGroup>
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </li>
                                    </ul>
                                    <Card.Body className="text-end">
                                        <Button variant="outline-secondary" className="me-2">
                                            {t('user.cancel')}
                                        </Button>
                                        <Button type="submit" variant="primary">
                                            {t('user.changePassword')}
                                        </Button>
                                    </Card.Body>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Tab.Pane>
        </React.Fragment>
    );
};

export default ChangePassword;